/**
 * <faq-topics>
 * Builds a sidebar navigation from the FAQ category blocks it contains, enables
 * smooth-scroll to each category, and highlights the active topic on scroll
 * (scroll-spy) via IntersectionObserver. Native, dependency-free.
 *
 * Graceful degradation: with JS disabled the sidebar list is simply empty and the
 * stacked categories (and their native <details> rows) remain fully usable.
 */
class FaqTopics extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('.faq-topics__nav-list');
    this.categories = Array.from(this.querySelectorAll('.faq-category'));

    if (!this.list || this.categories.length === 0) return;

    this.linkById = new Map();
    this.buildNav();
    this.observe();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  buildNav() {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const fragment = document.createDocumentFragment();

    for (const category of this.categories) {
      const id = category.id;
      const title = category.dataset.faqTitle || category.querySelector('.faq-category__title')?.textContent?.trim();
      if (!id || !title) continue;

      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = `#${id}`;
      link.textContent = title;
      link.addEventListener('click', (event) => {
        event.preventDefault();
        this.#scrollToCategory(category, reduceMotion);
        history.replaceState(null, '', `#${id}`);
      });

      item.appendChild(link);
      fragment.appendChild(item);
      this.linkById.set(id, link);
    }

    this.list.appendChild(fragment);
  }

  /**
   * Scroll offset (in px) needed to clear the sticky site header.
   * The header height is measured at runtime and exposed as `--header-height`
   * on <body> (see layout/theme.liquid), so this stays correct regardless of
   * how many rows the header has or how tall it renders. A small gap keeps the
   * category title from sitting flush against the header.
   * @returns {number}
   */
  #scrollOffset() {
    const headerHeight = parseInt(getComputedStyle(document.body).getPropertyValue('--header-height'), 10) || 0;
    return headerHeight + 24;
  }

  /**
   * Scrolls a category into view, offsetting for the sticky header so its title
   * stays fully visible instead of being hidden behind the header.
   * @param {HTMLElement} category
   * @param {boolean} reduceMotion
   */
  #scrollToCategory(category, reduceMotion) {
    const top = category.getBoundingClientRect().top + window.scrollY - this.#scrollOffset();
    window.scrollTo({ top: Math.max(top, 0), behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  observe() {
    const offset = this.#scrollOffset();

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.visibility ??= new Map();
          this.visibility.set(entry.target.id, entry.isIntersecting);
        }
        this.setActiveFromVisibility();
      },
      {
        // A category is "active" once its top passes the sticky offset band.
        rootMargin: `-${offset}px 0px -65% 0px`,
        threshold: 0,
      }
    );

    for (const category of this.categories) this.observer.observe(category);
  }

  setActiveFromVisibility() {
    // Pick the first category (in document order) currently within the band.
    const activeCategory = this.categories.find((category) => this.visibility?.get(category.id));
    const activeId = activeCategory ? activeCategory.id : this.categories[0]?.id;

    for (const [id, link] of this.linkById) {
      const isActive = id === activeId;
      link.classList.toggle('is-active', isActive);
      if (isActive) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    }
  }
}

if (!customElements.get('faq-topics')) {
  customElements.define('faq-topics', FaqTopics);
}
